import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatINR, formatDate, formatApiError } from "../lib/api";
import { PageHeader, StatusBadge, Empty } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Skeleton } from "../components/ui/skeleton";
import { Plus, Check, X } from "lucide-react";
import { toast } from "sonner";

const CATS = ["IT Services", "Logistics", "Marketing", "Legal", "HR", "Finance", "Manufacturing"];

export default function RFQManagement() {
  const [params] = useSearchParams();
  const preselected = params.get("vendor");
  const [rfqs, setRfqs] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [tab, setTab] = useState("Sent");
  const [openCreate, setOpenCreate] = useState(!!preselected);
  const [openCompare, setOpenCompare] = useState(null);

  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [cat, setCat] = useState("IT Services");
  const [deadline, setDeadline] = useState("");
  const [selVendors, setSelVendors] = useState(preselected ? [preselected] : []);

  const fetchAll = async () => {
    try {
      const [r, v] = await Promise.all([api.get("/rfqs"), api.get("/vendors")]);
      setRfqs(r.data || []);
      setVendors(v.data || []);
    } catch {
      setRfqs([]);
      toast.error("Could not load RFQs");
    }
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    if (preselected) {
      setSelVendors((p) => (p.includes(preselected) ? p : [preselected, ...p]));
      setOpenCreate(true);
    }
  }, [preselected]);

  const filtered = (rfqs || []).filter((r) => r.status === tab);

  const handleCreate = async () => {
    if (!title || !desc || !deadline || selVendors.length === 0) {
      toast.error("Please fill all fields and select at least 1 vendor"); return;
    }
    try {
      await api.post("/rfqs", { title, description: desc, category: cat, deadline: new Date(deadline).toISOString(), vendor_ids: selVendors });
      toast.success("RFQ sent");
      setOpenCreate(false);
      setTitle(""); setDesc(""); setDeadline(""); setSelVendors([]);
      fetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not send RFQ"); }
  };

  const handleAction = async (rid, resp_id, action) => {
    try {
      await api.post(`/rfqs/${rid}/responses/${resp_id}/action`, { action });
      toast.success(action === "accept" ? "Response accepted" : "Response rejected");
      fetchAll();
    } catch { toast.error("Could not update"); }
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="RFQ Management"
        subtitle="Send and track requests for quotation"
        actions={[
          <Button key="create-btn" className="bg-[#6366F1] hover:bg-[#4F46E5]" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTimeout(() => setOpenCreate(true), 0); }} data-testid="create-rfq-btn"><Plus className="w-4 h-4 mr-1.5" /> Create RFQ</Button>,
        ]}
      />

      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create new RFQ</DialogTitle>
                <DialogDescription>Send a request to your shortlisted vendors</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Cloud migration assessment" className="mt-1.5" data-testid="rfq-title" /></div>
                <div><Label>Description</Label><Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={4} placeholder="Detailed requirements..." className="mt-1.5" data-testid="rfq-description" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Category</Label>
                    <Select value={cat} onValueChange={setCat}>
                      <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Deadline</Label><Input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} className="mt-1.5" data-testid="rfq-deadline" /></div>
                </div>
                <div>
                  <Label>Vendors ({selVendors.length} selected)</Label>
                  <div className="mt-1.5 max-h-44 overflow-y-auto border rounded-md p-2 space-y-1">
                    {vendors.map((v) => (
                      <label key={v.id} className="flex items-center gap-2 px-2 py-1 hover:bg-zinc-50 rounded text-sm cursor-pointer">
                        <input type="checkbox" checked={selVendors.includes(v.id)} onChange={(e) => setSelVendors((p) => e.target.checked ? [...p, v.id] : p.filter((x) => x !== v.id))} />
                        <span>{v.name} <span className="text-xs text-zinc-400">· {v.category}</span></span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenCreate(false)}>Cancel</Button>
                <Button onClick={handleCreate} className="bg-[#6366F1] hover:bg-[#4F46E5]" data-testid="rfq-submit">Send RFQ</Button>
              </DialogFooter>
            </DialogContent>
      </Dialog>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="Draft">Draft</TabsTrigger>
          <TabsTrigger value="Sent" data-testid="tab-sent">Sent</TabsTrigger>
          <TabsTrigger value="Responses Received" data-testid="tab-responses">Responses Received</TabsTrigger>
          <TabsTrigger value="Closed">Closed</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-6">
          {rfqs === null && <Skeleton className="h-32" />}
          {rfqs && filtered.length === 0 && (
            <Empty title="No RFQs here" subtitle={`Your ${tab.toLowerCase()} RFQs will appear here`} />
          )}
          <div className="space-y-3">
            {filtered.map((r) => (
              <div key={r.id} className="bg-white border border-zinc-200 rounded-md p-5" data-testid="rfq-row">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{r.title}</h3>
                      <StatusBadge status={r.status} />
                    </div>
                    <p className="text-sm text-zinc-600 mt-1 line-clamp-2">{r.description}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500 mt-2">
                      <span>{r.category}</span>
                      <span>Deadline: {formatDate(r.deadline)}</span>
                      <span>{(r.vendor_ids || []).length} vendor{(r.vendor_ids || []).length === 1 ? "" : "s"}</span>
                      <span>{(r.responses || []).length} response{(r.responses || []).length === 1 ? "" : "s"}</span>
                    </div>
                  </div>
                  {(r.responses || []).length > 0 && (
                    <Button variant="outline" size="sm" onClick={() => { const item = r; setTimeout(() => setOpenCompare(item), 0); }} data-testid="compare-btn">Compare</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!openCompare} onOpenChange={(o) => !o && setOpenCompare(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Compare responses — {openCompare?.title}</DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b">
                <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
                  <th className="py-2">Vendor</th><th>Price</th><th>Timeline</th><th>Notes</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {(openCompare?.responses || []).map((resp) => (
                  <tr key={resp.id} className="border-b last:border-0" data-testid="response-row">
                    <td className="py-3 font-semibold">{resp.vendor_name}</td>
                    <td>{formatINR(resp.price)}</td>
                    <td>{resp.timeline_days} days</td>
                    <td className="max-w-xs truncate text-zinc-600">{resp.notes}</td>
                    <td><StatusBadge status={resp.status} /></td>
                    <td>
                      {resp.status === "Submitted" && (
                        <div className="flex gap-1">
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 px-2" onClick={() => handleAction(openCompare.id, resp.id, "accept")} data-testid="accept-response"><Check className="w-3 h-3" /></Button>
                          <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => handleAction(openCompare.id, resp.id, "reject")} data-testid="reject-response"><X className="w-3 h-3" /></Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
